import { useCallback, useRef, useState } from 'react';
import { setSongsSelected } from './songSelection';

export function itemsInRange(orderedItems, anchorId, clickedId) {
  const start = orderedItems.findIndex((item) => item.id === anchorId);
  const end = orderedItems.findIndex((item) => item.id === clickedId);
  if (end < 0) return [];
  if (start < 0) return [orderedItems[end]];
  return orderedItems.slice(Math.min(start, end), Math.max(start, end) + 1);
}

export function selectionForClick(orderedItems, anchorId, item, checked, selectRange) {
  const hasAnchor = orderedItems.some((candidate) => candidate.id === anchorId);
  const usedRange = selectRange && hasAnchor;
  return {
    items: usedRange ? itemsInRange(orderedItems, anchorId, item.id) : [item],
    checked,
    usedRange,
  };
}

export function selectionShouldCheck(currentIds, selection) {
  if (!selection.usedRange) return selection.checked;
  const selected = new Set(currentIds);
  return selection.items.some((item) => !selected.has(item.id));
}

export function updateSelectedIds(currentIds, selection) {
  const ids = selection.items.map((item) => item.id);
  if (selectionShouldCheck(currentIds, selection)) return [...new Set([...currentIds, ...ids])];
  const removed = new Set(ids);
  return currentIds.filter((id) => !removed.has(id));
}

export function updateSelectedItems(currentItems, selection) {
  return setSongsSelected(currentItems, selection.items,
    selectionShouldCheck(currentItems.map((item) => item.id), selection));
}

export default function useRangeSelection() {
  const anchors = useRef({});
  const [rangeMode, setRangeMode] = useState(null);

  const choose = (listKey, orderedItems, item, checked, shiftKey = false) => {
    const anchorId = anchors.current[listKey];
    const selection = selectionForClick(orderedItems, anchorId, item, checked, shiftKey || rangeMode === listKey);
    if (!selection.usedRange) anchors.current[listKey] = item.id;
    if (rangeMode === listKey && selection.usedRange) setRangeMode(null);
    return selection;
  };

  const toggleRangeMode = useCallback((listKey) => {
    delete anchors.current[listKey];
    setRangeMode((current) => current === listKey ? null : listKey);
  }, []);

  const resetRange = useCallback((listKey) => {
    delete anchors.current[listKey];
    setRangeMode((current) => current === listKey ? null : current);
  }, []);

  return { choose, toggleRangeMode, resetRange, isRangeMode: (listKey) => rangeMode === listKey };
}
